/**
 * SpotlightVariant — typewriter-style slogan reveal on a dark spotlit
 * stage. Editorial, "Apple keynote" feel.
 */
import { motion } from "framer-motion";
import Wordmark from "./Wordmark";
import { COPY } from "./copy";

const lineAnim = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.8, ease: "easeOut" },
});

export default function SpotlightVariant({ portrait }) {
  const headlineSize = portrait ? "text-[145px]" : "text-[125px]";

  return (
    <div className="relative w-full h-full overflow-hidden" style={{ background: "#0a0612" }}>
      <motion.div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 80% at 50% 40%, rgba(168,85,247,0.5) 0%, rgba(168,85,247,0.15) 35%, transparent 65%)",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0.85, 1] }}
        transition={{ duration: 4, delay: 0, times: [0, 0.3, 0.6, 1] }}
      />
      {[...Array(18)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1.5 h-1.5 rounded-full bg-purple-200/40"
          style={{ left: `${(i * 53) % 100}%`, top: `${(i * 37) % 100}%` }}
          animate={{ y: [0, -60, 0], opacity: [0.2, 0.8, 0.2] }}
          transition={{ duration: 5 + (i % 4), repeat: Infinity, delay: (i * 0.2) % 3, ease: "easeInOut" }}
        />
      ))}

      <motion.div
        className="absolute left-16 top-1/4 bottom-1/4 w-[3px] bg-gradient-to-b from-transparent via-purple-400 to-transparent"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{ delay: 0.4, duration: 0.9, ease: "easeOut" }}
      />

      <div className="absolute inset-0 flex flex-col justify-center px-24 text-white">
        <Wordmark
          size={portrait ? "xl" : "lg"}
          glpColor="#FFFFFF"
          floColor="#C4B5FD"
          align="left"
          delay={0.3}
        />
        <motion.h1
          {...lineAnim(1.0)}
          className={`${headlineSize} font-extrabold leading-[0.88] tracking-tight mt-6`}
        >
          {COPY.headline1}
          <br />
          <span className="text-purple-300">{COPY.headline2}</span>
        </motion.h1>
        <motion.p
          {...lineAnim(1.9)}
          className="text-3xl text-purple-100 mt-8 italic font-light"
        >
          {COPY.sub}
        </motion.p>
        <motion.div {...lineAnim(2.6)} className="mt-10 max-w-[800px]">
          <p className="text-xl text-white font-semibold mb-3">{COPY.body1}</p>
          <p className="text-lg text-purple-100/85 mb-3">{COPY.body2}</p>
          <p className="text-base text-purple-200/75 leading-relaxed">{COPY.body3}</p>
        </motion.div>
      </div>

      <motion.p
        {...lineAnim(3.4)}
        className="absolute bottom-14 left-24 text-sm tracking-[0.3em] uppercase text-purple-200/80"
      >
        {COPY.footer} ·{" "}
        <span className="text-white font-bold border-b border-purple-400/70 pb-0.5">{COPY.url}</span>
      </motion.p>
    </div>
  );
}
